import React, { useRef, useEffect, useLayoutEffect, useState, ReactNode } from 'react'
import i18next from 'i18next'
import { ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { CheckIcon } from '@heroicons/react/20/solid'

export interface AutocompleteOption {
  id: string
  label: string
  /** Optional color dot rendered before the label in the dropdown */
  dotColor?: string
  /** Optional round avatar rendered before the label (takes precedence over dotColor) */
  avatarUrl?: string
}

interface AutocompleteProps<T extends AutocompleteOption> {
  options: T[]
  value?: T
  onChange: (item: T | undefined) => void
  onQueryChange?: (query: string) => void
  loading?: boolean
  disabled?: boolean
  placeholder?: string
  icon?: ReactNode
  /** Tailwind left-padding class for the input when icon is present (default: 'pl-8') */
  iconPadding?: string
  emptyMessage?: string
  /** Optional color to tint the input background and border */
  tintColor?: string
  /** Show an X button to clear the selected value */
  clearable?: boolean
  /** Bind the dropdown to the input width and ellipsize long labels instead of growing to fit */
  fitWidth?: boolean
}

export function Autocomplete<T extends AutocompleteOption>({
  options,
  value,
  onChange,
  onQueryChange,
  loading = false,
  disabled = false,
  placeholder = 'Wybierz...',
  icon,
  iconPadding = 'pl-8',
  emptyMessage = 'Brak wyników',
  tintColor,
  clearable = false,
  fitWidth = false,
}: AutocompleteProps<T>) {
  const [query, setQuery] = useState(value?.label ?? '')
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  // Sync query when value changes externally
  useEffect(() => {
    setQuery(value?.label ?? '')
  }, [value?.id])

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightedIndex] as HTMLElement
      item?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex])

  // Reset highlight when options change
  useEffect(() => {
    setHighlightedIndex(-1)
  }, [options])

  const handleSelect = (item: T) => {
    onChange(item)
    setQuery(item.label)
    setIsOpen(false)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setQuery(val)
    onQueryChange?.(val)
    if (!isOpen) setIsOpen(true)
    if (value && val !== value.label) {
      onChange(undefined)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!isOpen) {
        setIsOpen(true)
      } else {
        setHighlightedIndex(Math.min(highlightedIndex + 1, options.length - 1))
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex(Math.max(highlightedIndex - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (isOpen && highlightedIndex >= 0 && options[highlightedIndex]) {
        handleSelect(options[highlightedIndex])
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      inputRef.current?.blur()
    }
  }

  const handleClick = () => {
    if (!disabled) {
      setIsOpen(true)
      inputRef.current?.select()
    }
  }

  const handleClear = () => {
    onChange(undefined)
    setQuery('')
    onQueryChange?.('')
    setIsOpen(false)
  }

  const showClear = clearable && !!value && !disabled

  // Reposition dropdown to fit within viewport (only when it can grow past the input)
  useLayoutEffect(() => {
    if (!isOpen || fitWidth || !listRef.current || !containerRef.current) return
    const list = listRef.current
    const container = containerRef.current
    // Reset position first to measure natural width
    list.style.left = '0px'
    list.style.right = 'auto'
    list.style.maxWidth = ''
    const containerRect = container.getBoundingClientRect()
    const listWidth = list.scrollWidth
    const viewportWidth = window.innerWidth
    const margin = 8
    // If list fits when left-aligned to the input
    if (containerRect.left + listWidth <= viewportWidth - margin) {
      list.style.left = '0px'
    } else {
      // Try right-aligning
      const rightAlignedLeft = containerRect.right - listWidth
      if (rightAlignedLeft >= margin) {
        list.style.left = `${rightAlignedLeft - containerRect.left}px`
      } else {
        // Clamp to viewport edges
        list.style.left = `${margin - containerRect.left}px`
        list.style.maxWidth = `${viewportWidth - margin * 2}px`
      }
    }
  }, [isOpen, options])

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        {icon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
            {icon}
          </div>
        )}
        <input
          ref={inputRef}
          type="text"
          className={`w-full rounded-md border-0 py-2.5 ${icon ? iconPadding : 'pl-3'} pr-8 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 ${
            disabled ? 'cursor-not-allowed opacity-60' : ''
          }`}
          style={
            tintColor
              ? {
                  backgroundColor: `${tintColor}10`,
                  boxShadow: `inset 0 0 0 1px ${tintColor}35`,
                  color: tintColor,
                }
              : undefined
          }
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          autoComplete="off"
        />
        {showClear ? (
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-2"
            onClick={handleClear}
            tabIndex={-1}
          >
            <XMarkIcon className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
          </button>
        ) : (
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-2"
            onClick={() => {
              if (!disabled) {
                setIsOpen(!isOpen)
                inputRef.current?.focus()
              }
            }}
            tabIndex={-1}
            disabled={disabled}
          >
            <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
          </button>
        )}
      </div>

      {isOpen && !disabled && (
        <ul
          ref={listRef}
          className={`absolute z-10 mt-1 max-h-48 overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none ${
            fitWidth ? 'w-full' : 'min-w-full w-max'
          }`}
        >
          {loading && options.length === 0 && (
            <li className="px-3 py-2 text-gray-500">{i18next.t('loading.searching')}</li>
          )}
          {!loading && options.length === 0 && (
            <li className="px-3 py-2 text-gray-500">{emptyMessage}</li>
          )}
          {options.map((item, index) => {
            const isSelected = value?.id === item.id
            const isHighlighted = highlightedIndex === index

            return (
              <li
                key={item.id}
                className={`relative cursor-pointer select-none py-2 pl-3 pr-8 ${
                  isHighlighted ? 'bg-indigo-600 text-white' : 'text-gray-900'
                }`}
                onMouseEnter={() => setHighlightedIndex(index)}
                onMouseLeave={() => setHighlightedIndex(-1)}
                onClick={() => handleSelect(item)}
              >
                <span className="flex min-w-0 items-center gap-2">
                  {item.avatarUrl ? (
                    <img
                      src={item.avatarUrl}
                      alt=""
                      loading="lazy"
                      className="h-8 w-8 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    item.dotColor && (
                      <svg className="h-1.5 w-1.5 shrink-0" viewBox="0 0 6 6" aria-hidden="true">
                        <circle cx={3} cy={3} r={3} fill={item.dotColor} />
                      </svg>
                    )
                  )}
                  <span className={`truncate ${isSelected ? 'font-semibold' : 'font-normal'}`}>
                    {item.label}
                  </span>
                </span>
                {isSelected && (
                  <span
                    className={`absolute inset-y-0 right-0 flex items-center pr-2 ${
                      isHighlighted ? 'text-white' : 'text-indigo-600'
                    }`}
                  >
                    <CheckIcon className="h-4 w-4" />
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
